import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useCallback,
  useState,
} from "react";
import { useUser } from "contexts/UserContext";
import querySessionTypes from "services/scheduler/querySessionTypes";
import { useSubscription } from "contexts/subscription-context";
import {
  isAIAudioSession,
  isAIVideoSession,
} from "components/ai-session-review/utils/getAISessionType";
import { useTeamContext } from "./group-practice-context/TeamContext";

type Context = {
  sessionTypes: SessionType[];
  setSessionTypes: React.Dispatch<React.SetStateAction<SessionType[]>>;
  busy: boolean;
  forceReload: () => void;
  setProviderId: (providerId: string | undefined) => void;
  providerId: string | undefined;
};
const SessionTypesContext = createContext<Context>({
  sessionTypes: [],
  setSessionTypes: () => undefined,
  busy: true,
  forceReload: () => null,
  setProviderId: () => undefined,
  providerId: undefined,
});

type Props = { children: ReactNode };

export const SessionTypesContextProvider = ({ children }: Props) => {
  const { user } = useUser();
  const { planType } = useSubscription();
  const [sessionTypes, setSessionTypes] = useState<SessionType[]>([]);
  const [busy, setBusy] = useState(true);
  const [reload, setReload] = useState(0);
  const forceReload = useCallback(() => setReload((s) => s + 1), []);
  const [providerId, setProviderId] = useState<string>();

  const { practiceOwnerOrchidUserId } = useTeamContext();

  // get sessionTypes when providerId changes or on init
  useEffect(() => {
    if (!user) {
      return;
    }
    setBusy(true);
    // Use providerId if set, otherwise fall back to practiceOwnerOrchidUserId
    const targetProviderId = providerId || practiceOwnerOrchidUserId;
    querySessionTypes(targetProviderId)
      .then((sessionTypes) => {
        if (planType !== "ai-only") {
          setSessionTypes(
            sessionTypes.filter(
              (p) => !isAIVideoSession(p.label) && !isAIAudioSession(p.label),
            ),
          );
        } else {
          setSessionTypes(sessionTypes);
        }
      })
      .catch(console.error)
      .finally(() => setBusy(false));
  }, [user, reload, planType, practiceOwnerOrchidUserId, providerId]);

  return (
    <SessionTypesContext.Provider
      value={{
        sessionTypes,
        setSessionTypes,
        busy,
        forceReload,
        providerId,
        setProviderId,
      }}
    >
      {children}
    </SessionTypesContext.Provider>
  );
};

export const useSessionTypes = () => useContext(SessionTypesContext);
